import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { ActivityAsyncAdapter } from './activity.adapter';

import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { AzureFunctionActivity, FunctionRunType } from 'app/model/pipeline/azure-function.activity';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import _ from 'lodash';
import { ReferenceAdapter } from 'app/model/adapters/reference.adapter';
import { AzureFunctionLinkedService } from 'app/model/linkedservice/azure-function.linkedservice';
import { toParameterClass, toParameterObject } from 'app/model/adapters/linkedservices/parameter-config.util';
import { convertParametersToModel, ModelConstants } from 'shell-models-lib';
import { FabricUserDataFunctionsLinkedService } from 'app/model/linkedservice/fabric-userdatafunctions.linkedservice';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';
import { ActivityConnectionContext, IAzureFunctionActivityContext } from 'app/interfaces/copilot.pipeline.action.types';
import { ModelService } from 'app/model/services/model.service';
import { findLinkedServiceFromContext } from 'app/shared/utils/update-pipeline-skill.util';
import { IFrameService } from 'app/services/iframe.service';
import { getFabricLsForParameterizedConnections } from '../utils';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureFunctionActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        linkedServiceName: '*',
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            functionName: '*',
            method: '*',
            headers: '*',
            body: '*',
            workspaceId: '*',
            functionSetId: '*',
            parameters: '*'
        },
        externalReferences: '*'
    }
})
export class AzureFunctionActivityAdapter extends ActivityAsyncAdapter<
    AzureFunctionActivity,
    Model.AzureFunctionActivity,
    IAzureFunctionActivityContext
> {
    public model(): IModel {
        return Metamodel.modelFor(AzureFunctionActivity);
    }

    public override async toFabricCopilotContext(
        activity: AzureFunctionActivity
    ): Promise<IAzureFunctionActivityContext> {
        if (activity) {
            const activityContext: IAzureFunctionActivityContext = await super.toFabricCopilotContext(activity);
            if (activity.linkedService) {
                const lsAdapter = this.context.asyncAdapterFor<
                    AzureFunctionLinkedService,
                    Model.LinkedServiceResource,
                    ActivityConnectionContext
                >(Metamodel.modelOf(activity.linkedService));
                activityContext.Connection = await lsAdapter?.toFabricCopilotContext(activity.linkedService);
            }
            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: IAzureFunctionActivityContext,
        model?: AzureFunctionActivity
    ): Promise<AzureFunctionActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            if (context.Connection) {
                const modelService = await getServiceInstance<ModelService>(this.context.contextData, 'ModelService');
                const iframeService = await getServiceInstance<IFrameService>(
                    this.context.contextData,
                    'IFrameService'
                );
                model.linkedService = (
                    await findLinkedServiceFromContext<AzureFunctionLinkedService>(context.Connection, {
                        modelService,
                        iframeService
                    })
                ).linkedService;
                if (model.linkedService) {
                    const lsAdapter = Metamodel.isModel(context.Connection.Type)
                        ? this.context.asyncAdapterFor<
                              AzureFunctionLinkedService,
                              Model.LinkedServiceResource,
                              ActivityConnectionContext
                          >(Metamodel.modelByName(context.Connection.Type))
                        : undefined;
                    await lsAdapter?.fromFabricCopilotContext(context.Connection, model.linkedService);
                }
            }
        }
        return model;
    }

    protected async _toResource(activity: AzureFunctionActivity): Promise<Model.AzureFunctionActivity> {
        const exposureControlService = await getServiceInstance<ExposureControlService>(
            this.context.contextData,
            'ExposureControlService'
        );
        const modelAzureFunctionActivity: Model.AzureFunctionActivity = super._getCommonActivityProperties(
            activity,
            Metamodel.modelFor(AzureFunctionActivity).name
        ) as Model.AzureFunctionActivity;
        const azureFunctionActivity: AzureFunctionActivity = activity as AzureFunctionActivity;

        modelAzureFunctionActivity.typeProperties = {
            functionName: ValueAdapter.toResource(activity, AzureFunctionActivity.FUNCTIONNAME)
        };

        if (activity.runType == FunctionRunType.AZURE_FUNCTION) {
            const headers = toParameterObject(activity.headers);
            modelAzureFunctionActivity.typeProperties.body = ValueAdapter.toResource(
                activity,
                AzureFunctionActivity.BODY
            );
            modelAzureFunctionActivity.typeProperties.headers = headers ? headers : {};
            modelAzureFunctionActivity.typeProperties.method = activity.method || undefined;

            if (azureFunctionActivity.linkedService) {
                modelAzureFunctionActivity.linkedServiceName = this._toLinkedServiceReferenceModel(
                    azureFunctionActivity.linkedService,
                    azureFunctionActivity.linkedServiceParameters
                );
            }
        } else if (activity.runType == FunctionRunType.FABRIC_FUNCTION) {
            const parameters = toParameterObject(activity.parameters);
            modelAzureFunctionActivity.typeProperties.parameters = parameters ? parameters : {};
            modelAzureFunctionActivity.typeProperties.functionSetId = ValueAdapter.toResource(
                activity,
                AzureFunctionActivity.FUNCTIONSETID
            );
            modelAzureFunctionActivity.typeProperties.workspaceId = ValueAdapter.toResource(
                activity,
                AzureFunctionActivity.WORKSPACEID
            );

            if (exposureControlService.UseTokenServiceInDFActivity && azureFunctionActivity.udfLinkedService) {
                modelAzureFunctionActivity.linkedServiceName = this._toLinkedServiceReferenceModel(
                    azureFunctionActivity.udfLinkedService,
                    azureFunctionActivity.linkedServiceParameters
                );
            }
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            // Overlay this on http activity
            _.merge(modelAzureFunctionActivity, parsed);
        }

        return modelAzureFunctionActivity;
    }

    protected async _toModel(modelActivity: Model.AzureFunctionActivity): Promise<AzureFunctionActivity> {
        const activity: AzureFunctionActivity = AzureFunctionActivity.MODEL.create<AzureFunctionActivity>(
            modelActivity.name,
            modelActivity.description
        );
        const modelAzureFunctionActivity = modelActivity as Model.AzureFunctionActivity;
        const exposureControlService = await getServiceInstance<ExposureControlService>(
            this.context.contextData,
            'ExposureControlService'
        );

        this._updatedCommonProperties(modelActivity, activity);

        if (modelAzureFunctionActivity.typeProperties) {
            ValueAdapter.toModel(
                activity,
                AzureFunctionActivity.FUNCTIONNAME,
                modelAzureFunctionActivity.typeProperties.functionName
            );

            if (modelAzureFunctionActivity.typeProperties.method) {
                ValueAdapter.toModel(
                    activity,
                    AzureFunctionActivity.BODY,
                    modelAzureFunctionActivity.typeProperties.body
                );
                activity.method = modelAzureFunctionActivity.typeProperties.method
                    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (modelAzureFunctionActivity.typeProperties.method.toUpperCase() as any)
                    : undefined;
                activity.headers = toParameterClass(modelActivity.typeProperties.headers, activity);
                activity.runType = FunctionRunType.AZURE_FUNCTION;
                if (modelAzureFunctionActivity.linkedServiceName !== undefined) {
                    const ls = getFabricLsForParameterizedConnections(
                        modelAzureFunctionActivity.linkedServiceName,
                        ModelConstants.azureFunctionLinkedService,
                        activity
                    );
                    activity.linkedService = ls ? (ls as AzureFunctionLinkedService) : ReferenceAdapter.toProxyModel<AzureFunctionLinkedService>(
                        Metamodel.modelFor(AzureFunctionLinkedService),
                        modelAzureFunctionActivity.linkedServiceName,
                        this.context
                    );

                    if (modelAzureFunctionActivity.linkedServiceName.parameters) {
                        convertParametersToModel(
                            activity,
                            !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                            activity.linkedService,
                            activity.linkedServiceParameters,
                            modelAzureFunctionActivity.linkedServiceName.parameters
                        );
                    }
                }
            } else if (modelAzureFunctionActivity.typeProperties.functionSetId) {
                activity.parameters = toParameterClass(modelActivity.typeProperties.parameters, activity);
                ValueAdapter.toModel(
                    activity,
                    AzureFunctionActivity.FUNCTIONSETID,
                    modelAzureFunctionActivity.typeProperties.functionSetId
                );
                ValueAdapter.toModel(
                    activity,
                    AzureFunctionActivity.WORKSPACEID,
                    modelAzureFunctionActivity.typeProperties.workspaceId
                );
                if (
                    exposureControlService.UseTokenServiceInDFActivity &&
                    modelAzureFunctionActivity.linkedServiceName !== undefined
                ) {
                    const udfLs = getFabricLsForParameterizedConnections(
                        modelAzureFunctionActivity.linkedServiceName,
                        ModelConstants.udfLinkedService,
                        activity
                    );
                    activity.udfLinkedService = udfLs ? (udfLs as FabricUserDataFunctionsLinkedService) : ReferenceAdapter.toProxyModel<FabricUserDataFunctionsLinkedService>(
                        Metamodel.modelFor(FabricUserDataFunctionsLinkedService),
                        modelAzureFunctionActivity.linkedServiceName,
                        this.context
                    );

                    if (modelAzureFunctionActivity.linkedServiceName.parameters) {
                        convertParametersToModel(
                            activity,
                            !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                            activity.udfLinkedService,
                            activity.linkedServiceParameters,
                            modelAzureFunctionActivity.linkedServiceName.parameters
                        );
                    }
                }
                activity.runType = FunctionRunType.FABRIC_FUNCTION;
            }
        }

        activity.raw = JSON.stringify(
            await ValueAdapter.remainder(this.context, modelAzureFunctionActivity, AzureFunctionActivity.MODEL)
        );

        return activity;
    }
}
